import React, { useMemo, useRef, useState } from 'react'
import styles from "../css/Settings.module.css"
import { IoEyeOffOutline } from "react-icons/io5";
import { IoEyeOutline } from "react-icons/io5";
import validator from "validator"
import LoadingButton from '../../Loaders/js/LoadingButton';
import { AiOutlineArrowLeft, AiOutlineArrowRight, AiOutlineCheck } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import { AUTH_RESET, AUTH_SUCCESS } from '../../../actions/authTypes';
import { updateEmail } from '../../../actions/authActions';
import { useEffect } from 'react';
import useOverflow from '../../../hooks/useOverflow';
import DemoModeBanner from './DemoModeBanner';

const Email = ({goBack}) => {
    const [data,setData] = useState({current_password:"",email:"",confirm_email:""})
    const [showPassword,setShowPassword] = useState(false)
    const [errors,setErrors] = useState({})
    const user = useSelector((state) => state.authState.user)
    const loading = useSelector((state) => state.authState.loading)
    const success = useSelector((state) => state.authState.authSuccess)
    const dispatch = useDispatch()
    const contentRef = useRef(null)
    const hasOverflow = useOverflow(contentRef)

    useEffect(()=>{
        return () => {
            dispatch({type: AUTH_RESET})
        }
    },[dispatch])

    const resetErrors = (name,value) => {
        if (name === "email") {
            if (errors.email && validator.isEmail(value)) {
                setErrors((prevErrors)=>{
                    return {...prevErrors,email: null}
                })
            }
            if (errors.confirm_email && value === data.confirm_email) {
                setErrors((prevErrors)=>{
                    return {...prevErrors,confirm_email: null}
                })
            }
        }
        if (name === "confirm_email") {
            if (errors.confirm_email && value === data.email) {
                setErrors((prevErrors)=>{
                    return {...prevErrors,confirm_password: null}
                })
            }
        }
        if (name === "current_password" && errors.current_password) {
            setErrors((prevErrors)=>{
                return {...prevErrors,current_password: null}
            })
        }
    }

    const handleChange = (e) => {
        let name = e.target.name
        let value = e.target.value
        setData({...data,[name]:value})
        resetErrors(name,value)
        if (success){
            dispatch({type: AUTH_SUCCESS,payload:false})
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        document.activeElement.blur()
        let valid = true
        if (!validator.isEmail(data.email)){
          valid = false
          setErrors((prevErrors)=> {return {...prevErrors,email:"Invalid email address"}})
        }
        if (data.email !== data.confirm_email){
          valid = false
          setErrors((prevErrors)=> {return {...prevErrors,confirm_email:"Emails do not match"}})
        }
        if (valid){
            try{
                await dispatch(updateEmail({current_password:data.current_password, email: data.email}))
                setData({current_password:"",email:"",confirm_email:""})
            }
            catch(err){
                setErrors({...errors,...err.response.data})
            }
        }
    }

    const isDisabled = useMemo(()=>{
        if (!data.confirm_email?.trim() || !data.email?.trim() || !data.current_password){
            return true
        }
        else if (Object.values(errors).some(err => err !== null)){
            return true
        }
        else{
            return user?.isDemoUser
        }
    },[data,errors,user?.isDemoUser])

    return (
        <form className = {`${styles.form} ${hasOverflow ? styles.overflowed : ""} nested-container`} onSubmit = {handleSubmit} autoComplete='off'>
            <div className = {styles.subpageHeader}>
                <button type = "button" className = {styles.back} onClick = {()=>goBack()}>
                    <AiOutlineArrowLeft/>
                </button>
                <div>
                <div className = {styles.subpageTitle}>Change Email Address</div>
                <div className = {styles.subpageDesc}>
                    Update the email for your NotePro account. Your email is currently{" "}
                    <strong data-testid = "current-email" >{user.email}</strong>.
                </div>
                </div>
            </div>
            {user.isDemoUser && <DemoModeBanner tab = "email"/>}
            <div className = {`${styles.body} nested-container`} ref = {contentRef}>
                {success && <div data-testid = "success-message" className = {styles.successBanner}>
                    <div className = {styles.checkIcon}><AiOutlineCheck/></div>
                    <div className = {styles.successText}>Email changed successfully</div>
                </div>}
                <div className = {`${styles.field} ${errors.current_password ? styles.errorBox : ""}`}>
                    <label htmlFor = "current_password" className = {styles.label}>Current Password</label>
                    <div className = {styles.inputWrap}>
                        <div className = {styles.inputContainer}>
                            <input
                                className = {styles.input}
                                type = {showPassword ? "text" : "password"}
                                name = "current_password"
                                value = {data.current_password}
                                onChange = {handleChange}
                                placeholder="Enter your current password"
                                disabled = {user.isDemoUser}
                            />
                        </div>
                        {!user.isDemoUser && <button type = "button" className = {styles.inputIcon} onClick = {()=>setShowPassword(!showPassword)}>
                            {showPassword ? <IoEyeOffOutline/> : <IoEyeOutline/>}
                        </button>}
                    </div>
                    {errors.current_password && <div data-testid = "current-password-error" className = {styles.error}>{errors.current_password}</div>}
                </div>
                <div className = {`${styles.field} ${errors.email ? styles.errorBox : ""}`}>
                    <label htmlFor = "email" className = {styles.label}>New Email</label>
                    <div className = {styles.inputContainer}>
                        <input
                            className = {styles.input}
                            type = "text" 
                            name = "email" 
                            value = {data.email} 
                            onChange = {handleChange}
                            placeholder = "Enter new email address"
                            disabled = {user.isDemoUser}
                        />
                    </div>
                    {errors.email && <div data-testid = "email-error" className = {styles.error}>{errors.email}</div>}
                </div>
                <div className = {`${styles.field} ${errors.confirm_email ? styles.errorBox : ""}`}>
                    <label htmlFor = "confirm_email" className = {styles.label}>Confirm New Email</label>
                    <div className = {styles.inputContainer}>
                        <input
                            className = {styles.input}
                            type = "text" 
                            name = "confirm_email" 
                            value = {data.confirm_email} 
                            onChange = {handleChange}
                            placeholder = "Re-enter new email address"
                            disabled = {user.isDemoUser}
                        />
                    </div>
                    {errors.confirm_email && <div data-testid = "confirm-email-error" className = {styles.error}>{errors.confirm_email}</div>}
                    <div className = {styles.hint}>
                        Make sure you have access to this email before saving.
                    </div>
                </div>
            </div>
            <div className = {styles.footer}>
                <button type = "button" className = {styles.cancelBtn} onClick = {()=>goBack()}>Cancel</button>
                <button type = "submit" className = {`${styles.submitBtn} ${loading ? styles.loading : ""}`} disabled = {isDisabled}>
                    Change Email
                    <div className = {styles.btnIcon}>
                        <AiOutlineArrowRight/>
                    </div>
                    {loading && <LoadingButton/>}
                </button>
            </div>
        </form>
  )
}

export default Email